import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/components/Common";
import { Page, PageHeader, Loading, ErrorNote, Medallion, RichText } from "@/components/Common";

export const CATEGORIES = [
  { key: "", label: "سب" },
  { key: "poet_classical", label: "کلاسیکی شعراء" },
  { key: "poet_modern", label: "جدید شعراء" },
  { key: "poet_contemporary", label: "معاصر شعراء" },
  { key: "afsana", label: "افسانہ نگار" },
  { key: "novel", label: "ناول نگار" },
  { key: "naqid", label: "ناقدین" },
  { key: "other", label: "دیگر ادیب" },
];

export const categoryLabel = (key) => CATEGORIES.find((c) => c.key === key)?.label || "ادیب";

export function AuthorsPage() {
  const [authors, setAuthors] = useState(null);
  const [cat, setCat] = useState("");

  useEffect(() => {
    setAuthors(null);
    api.get("/authors", { params: cat ? { category: cat } : {} }).then((r) => setAuthors(r.data)).catch(() => setAuthors([]));
  }, [cat]);

  return (
    <Page>
      <PageHeader title="شعراء و ادباء" subtitle="اردو کے بزرگ و معاصر اہلِ قلم کی حیات و خدمات" crumbs={[{ label: "شعراء و ادباء" }]} />
      <div className="flex flex-wrap gap-2 mb-8" data-testid="author-category-tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`author-tab-${c.key || "all"}`}
            onClick={() => setCat(c.key)}
            className={`px-5 py-1.5 rounded-full border text-base leading-[2] transition-colors ${
              cat === c.key ? "bg-[#2a1508] text-[#f5efe3] border-[#2a1508]" : "border-[#c5a059]/60 text-[#5c3a21] hover:border-[#d4af37]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      {!authors ? (
        <Loading />
      ) : authors.length === 0 ? (
        <p className="text-center text-lg text-[#5c3a21] py-16" data-testid="authors-empty">اس زمرے میں فہرست جلد شامل کی جائے گی</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="authors-grid">
          {authors.map((a) => (
            <Link
              key={a.slug}
              to={`/authors/${a.slug}`}
              data-testid={`author-card-${a.slug}`}
              className="group bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(42,21,8,0.12)] hover:border-[#d4af37]"
            >
              <Medallion name={a.name} className="w-20 h-20 mx-auto text-4xl" />
              <h3 className="mt-4 text-xl font-semibold text-[#2a1508] leading-[2]">{a.name}</h3>
              <p className="text-sm text-[#5c3a21] mt-1 font-ui">{a.years}</p>
              <p className="text-sm text-[#c5a059] mt-1 leading-[2]">{categoryLabel(a.category)}</p>
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

export function AuthorPage() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setAuthor(null);
    setErr(false);
    api.get(`/authors/${slug}`).then((r) => setAuthor(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <Page><ErrorNote message="شاعر / ادیب نہیں ملا" /></Page>;
  if (!author) return <Page><Loading /></Page>;

  return (
    <Page>
      <PageHeader title={author.name} crumbs={[{ label: "شعراء و ادباء", to: "/authors" }, { label: author.name }]} />
      <div className="grid md:grid-cols-3 gap-8 items-start">
        <div className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-2xl p-8 text-center md:sticky md:top-32" data-testid="author-profile-card">
          <Medallion name={author.name} className="w-32 h-32 mx-auto text-6xl" />
          <h2 className="mt-5 text-2xl font-bold text-[#2a1508] leading-[2]">{author.name}</h2>
          <p className="font-ui text-[#5c3a21] mt-1" data-testid="author-years">{author.years}</p>
          <div className="mt-4 space-y-2 text-base text-[#5c3a21] leading-[2]">
            <p><span className="font-semibold text-[#2a1508]">زمرہ:</span> {categoryLabel(author.category)}</p>
            {author.birthplace && <p><span className="font-semibold text-[#2a1508]">جائے پیدائش:</span> {author.birthplace}</p>}
          </div>
          {author.contribution && (
            <p className="mt-5 pt-4 border-t border-[#d4af37]/30 text-base leading-[2.2] text-[#4a2b16]">{author.contribution}</p>
          )}
        </div>

        <div className="md:col-span-2 space-y-8">
          <div className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-2xl p-6 sm:p-10" data-testid="author-bio">
            <h3 className="text-2xl font-bold text-[#2a1508] leading-[2] mb-2">تعارف و حیات</h3>
            <RichText text={author.bio} />
          </div>

          {author.syllabus && (
            <div className="bg-[#e8dfcc] border border-[#c5a059]/60 rounded-2xl p-6" data-testid="author-syllabus">
              <h3 className="text-xl font-bold text-[#2a1508] leading-[2]">prescribed کلام (UGC NET)</h3>
              <p className="mt-2 text-lg text-[#4a2b16] leading-[2.2]">{author.syllabus}</p>
            </div>
          )}

          <div data-testid="author-works">
            <h3 className="text-2xl font-bold text-[#2a1508] leading-[2] mb-4">تصانیف و منتخب کلام</h3>
            {author.works?.length ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {author.works.map((w) => (
                  <Link
                    key={w.slug}
                    to={`/works/${w.slug}`}
                    data-testid={`author-work-${w.slug}`}
                    className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl px-5 py-4 text-lg text-[#2a1508] leading-[2] hover:border-[#d4af37] hover:-translate-y-0.5 transition-all duration-300"
                  >
                    {w.title}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-lg text-[#5c3a21] bg-[#fdfbf7] border border-dashed border-[#c5a059]/60 rounded-xl p-5" data-testid="author-works-empty">
                متون جلد شامل کیے جائیں گے
              </p>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
